import { GetExploitationResponse, GetUserResponseLite } from 'src/app/services/dtos';
import {
	areDatesEqual,
	areDatesSameMonthAndYear,
	formatDateDay,
	getDatesOfLastSevenDays,
	getDatesOfLastThirtyDays,
	getDatesOfLastTwelveMonths,
	isDateOlder,
} from 'src/app/utils/date';

interface ChartPieDataset {
	data: number[];
	backgroundColor: string[];
}

interface ChartLabels {
	fontColor: string;
}

interface ChartLegend {
	position?: string;
	labels: ChartLabels;
}

interface ChartTitle {
	display: boolean;
	text: string;
	fontSize?: number;
	position?: string;
}

interface ChartPlugins {
	title?: ChartTitle;
	legend: ChartLegend;
}

interface ChartLineDataset {
	label: string;
	data: number[];
	borderColor: string;
	backgroundColor: string;
	fill: boolean;
	tension: number;
}

interface ChartTicks {
	color: string;
}

interface ChartGrid {
	color: string;
}

interface ChartX {
	ticks: ChartTicks;
	grid: ChartGrid;
}

interface ChartY {
	ticks: ChartTicks;
	grid: ChartGrid;
}

interface ChartScales {
	x: ChartX;
	y: ChartY;
}

export enum ChartColorPalette {
	OCEANBLUE = 'rgb(54, 162, 235)',
	CRIMSONRED = 'rgb(255, 99, 132)',
	SUNSHINEYELLOW = 'rgb(255, 205, 86)',
	TEALGREEN = 'rgb(75, 192, 192)',
	ROYALPURPLE = 'rgb(153, 102, 255)',
	TANGERINEORANGE = 'rgb(255, 153, 51)',
	SKYBLUE = 'rgb(102, 204, 255)',
	BUBBLEGUMPINK = 'rgb(255, 102, 204)',
	SLATEGRAY = 'rgb(128, 128, 128)',
}

export enum ChartDisplayType {
	WEEK = 'week',
	MONTH = 'month',
	YEAR = 'year',
}

export enum ChartDisplayPeriod {
	WEEK = '7 derniers jours',
	MONTH = '30 derniers jours',
	YEAR = '12 derniers mois',
}

export enum FilterOptionLabels {
	IDLE = 'Non synchronisé',
	SYNCHRONIZED = 'Synchronisé',
	SCHEDULED = 'Synchronisation plannifiée',
}

export interface FilterPieOptions {
	label: FilterOptionLabels;
	code: string;
}

export interface ChartPieOptions {
	plugins: ChartPlugins;
}

export interface ChartPieData {
	labels: string[];
	datasets: ChartPieDataset[];
}

export interface ChartLinearData {
	labels: string[];
	datasets: ChartLineDataset[];
}

export interface ChartLinearOptions {
	plugins: ChartPlugins;
	scales: ChartScales;
}

export interface ChartOverviewPeriod {
	period: string;
	code: ChartDisplayType;
}

export function createLinearData(labels: string[], datasetLabel: string, data: number[]) {
	return {
		labels: labels,
		datasets: [
			{
				label: datasetLabel,
				data: data,
				fill: false,
				backgroundColor: ChartColorPalette.CRIMSONRED,
				borderColor: ChartColorPalette.OCEANBLUE,
				tension: 0.4,
			},
		],
	};
}

export function createLinearOptions(text: string, titlePosition?: string, legendPosition?: string) {
	return {
		plugins: {
			title: {
				text: text,
				display: true,
				position: titlePosition || 'bottom',
			},
			legend: {
				labels: {
					position: legendPosition,
					fontColor: '#A0A7B5',
				},
			},
		},
		scales: {
			x: {
				ticks: {
					color: '#A0A7B5',
				},
				grid: {
					color: 'rgba(160, 167, 181, .3)',
				},
			},
			y: {
				ticks: {
					stepSize: '1',
					color: '#A0A7B5',
				},
				grid: {
					color: 'rgba(160, 167, 181, .3)',
				},
			},
		},
	};
}

export function createPieData(labels: string[], data: number[], backgroundColor: string[]) {
	return {
		labels: labels,
		datasets: [
			{
				data: data,
				backgroundColor: backgroundColor,
			},
		],
	};
}

export function createPieOptions(text: string, titlePosition?: string, legendPosition?: string) {
	return {
		plugins: {
			title: {
				display: true,
				text: text,
				position: titlePosition || 'bottom',
			},
			legend: {
				position: legendPosition || 'right',
				labels: {
					fontColor: '#A0A7B5',
				},
			},
		},
	};
}

export function getNumberOfCheptelForThe(
	chartDisplayType: ChartDisplayType,
	cheptels: GetExploitationResponse[],
): number[] {
	const answer = [];
	if (chartDisplayType === 'week') {
		const datesOfLastSevenDays = getDatesOfLastSevenDays();
		for (let i = 0; i < datesOfLastSevenDays.length; i++) {
			answer.push(
				cheptels.filter((cheptel) => areDatesEqual(formatDateDay(cheptel.created), datesOfLastSevenDays[i]))
					.length,
			);
		}
	} else if (chartDisplayType === 'month') {
		const datesOfLastThirtyDays = getDatesOfLastThirtyDays();
		for (let i = 0; i < datesOfLastThirtyDays.length; i++) {
			answer.push(
				cheptels.filter((cheptel) => areDatesEqual(formatDateDay(cheptel.created), datesOfLastThirtyDays[i]))
					.length,
			);
		}
	} else if (chartDisplayType === 'year') {
		const datesOfLastTwelveMonth = getDatesOfLastTwelveMonths();
		for (let i = 0; i < datesOfLastTwelveMonth.length; i++) {
			answer.push(
				cheptels.filter((cheptel) =>
					areDatesSameMonthAndYear(formatDateDay(cheptel.created), datesOfLastTwelveMonth[i]),
				).length,
			);
		}
	}
	return answer;
}

export function getNumberOfUserForThe(chartDisplayType: ChartDisplayType, users: GetUserResponseLite[]): number[] {
	const answer = [];
	if (chartDisplayType === 'week') {
		const datesOfLastSevenDays = getDatesOfLastSevenDays();
		for (let i = 0; i < datesOfLastSevenDays.length; i++) {
			answer.push(
				users.filter((user) => areDatesEqual(formatDateDay(user.created), datesOfLastSevenDays[i])).length,
			);
		}
	} else if (chartDisplayType === 'month') {
		const datesOfLastThirtyDays = getDatesOfLastThirtyDays();
		for (let i = 0; i < datesOfLastThirtyDays.length; i++) {
			answer.push(
				users.filter((user) => areDatesEqual(formatDateDay(user.created), datesOfLastThirtyDays[i])).length,
			);
		}
	} else if (chartDisplayType === 'year') {
		const datesOfLastTwelveMonth = getDatesOfLastTwelveMonths();
		for (let i = 0; i < datesOfLastTwelveMonth.length; i++) {
			answer.push(
				users.filter((user) => areDatesSameMonthAndYear(formatDateDay(user.created), datesOfLastTwelveMonth[i]))
					.length,
			);
		}
	}
	return answer;
}

export function getNumberOfTotalUserForThe(chartDisplayType: ChartDisplayType, users: GetUserResponseLite[]): number[] {
	const answer = [];
	if (chartDisplayType === 'week') {
		const datesOfLastSevenDays = getDatesOfLastSevenDays();

		for (let i = 0; i < datesOfLastSevenDays.length; i++) {
			answer.push(
				users.filter((user) => isDateOlder(formatDateDay(user.created), datesOfLastSevenDays[i])).length,
			);
		}
	} else if (chartDisplayType === 'month') {
		const datesOfLastThirtyDays = getDatesOfLastThirtyDays();
		for (let i = 0; i < datesOfLastThirtyDays.length; i++) {
			answer.push(
				users.filter((user) => isDateOlder(formatDateDay(user.created), datesOfLastThirtyDays[i])).length,
			);
		}
	} else if (chartDisplayType === 'year') {
		const datesOfLastTwelveMonth = getDatesOfLastTwelveMonths();
		for (let i = 0; i < datesOfLastTwelveMonth.length; i++) {
			answer.push(
				users.filter((user) => isDateOlder(formatDateDay(user.created), datesOfLastTwelveMonth[i])).length,
			);
		}
	}
	return answer;
}

export function getNumberOfTotalCheptelForThe(
	chartDisplayType: ChartDisplayType,
	exploitations: GetExploitationResponse[],
): number[] {
	const answer = [];
	if (chartDisplayType === 'week') {
		const datesOfLastSevenDays = getDatesOfLastSevenDays();

		for (let i = 0; i < datesOfLastSevenDays.length; i++) {
			answer.push(
				exploitations.filter((exploitation) =>
					isDateOlder(formatDateDay(exploitation.created), datesOfLastSevenDays[i]),
				).length,
			);
		}
	} else if (chartDisplayType === 'month') {
		const datesOfLastThirtyDays = getDatesOfLastThirtyDays();
		for (let i = 0; i < datesOfLastThirtyDays.length; i++) {
			answer.push(
				exploitations.filter((exploitation) =>
					isDateOlder(formatDateDay(exploitation.created), datesOfLastThirtyDays[i]),
				).length,
			);
		}
	} else if (chartDisplayType === 'year') {
		const datesOfLastTwelveMonth = getDatesOfLastTwelveMonths();
		for (let i = 0; i < datesOfLastTwelveMonth.length; i++) {
			answer.push(
				exploitations.filter((exploitation) =>
					isDateOlder(formatDateDay(exploitation.created), datesOfLastTwelveMonth[i]),
				).length,
			);
		}
	}
	return answer;
}
